import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { apiKey } from "../../config/config";
import { languageHelper } from "../../config/helperFunctions";
import { FaUser, FaLock, FaShieldAlt, FaEye, FaEyeSlash } from "react-icons/fa";

function AdministrationLogin() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const handleInput = (e) =>
    setCredentials((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);

    axios
      .post(`${apiKey}/api/administration/login`, credentials, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        if (res.data?.success) {
          localStorage.setItem("administration-token", res.data.data.token);
          localStorage.setItem("administration-user", JSON.stringify(res.data.data.user));

          toast.success(languageHelper(res.data.message || "Administration login successful"), {
            position: "bottom-right",
            autoClose: 4000,
            theme: "colored",
          });

          navigate("/administration/dashboard");
        } else {
          toast.error(languageHelper(res.data?.message || "Login failed"), {
            position: "bottom-right",
            autoClose: 5000,
            theme: "colored",
          });
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          languageHelper(err?.response?.data?.message || "Invalid developer username or password"),
          {
            position: "bottom-right",
            autoClose: 5000,
            theme: "colored",
          }
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = localStorage.getItem("administration-token");
    if (token) {
      navigate("/administration/dashboard");
    } else {
      setPageLoading(false);
    }
  }, [navigate]);

  if (pageLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F4F4F8]">
        <img src="/assets/gif/dash2.gif" alt="Loading" />
      </div>
    );
  }

  return (
    <main className="min-h-screen w-full grid place-items-center px-4 bg-[#F4F4F8] font-sans">
      {/* Developer Access Portal Login Card */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-purple-900/10 max-w-[450px] w-full overflow-hidden border border-slate-100">
        {/* Header Section */}
        <div className="bg-[#7029C4] py-7 px-6 text-center text-white flex flex-col items-center justify-center">
          <div className="flex items-center justify-center gap-2 text-xl font-bold font-mono">
            <span className="font-extrabold text-2xl text-purple-200 tracking-wider">&lt;/&gt;</span>
            <h1 className="text-xl font-extrabold tracking-tight text-white font-sans">
              {languageHelper("Developer Access Portal")}
            </h1>
          </div>
          <p className="text-xs text-purple-100/90 font-medium mt-1 tracking-wide">
            {languageHelper("Authorized Developer Session Authentication")}
          </p>
        </div>

        {/* Form Body */}
        <div className="p-8 space-y-6">
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Developer Username Field */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-800 mb-2">
                <FaUser className="text-[#7029C4] text-xs" />
                <span>{languageHelper("Developer Username")}</span>
              </label>
              <input
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleInput}
                placeholder={languageHelper("Enter developer username")}
                required
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#7029C4] focus:ring-2 focus:ring-[#7029C4]/20 outline-none text-sm text-slate-800 bg-white placeholder:text-slate-400 transition-all shadow-sm"
              />
            </div>

            {/* Developer Password Field */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-800 mb-2">
                <FaLock className="text-[#7029C4] text-xs" />
                <span>{languageHelper("Developer Password")}</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={handleInput}
                  placeholder={languageHelper("Enter developer password")}
                  required
                  className="w-full pl-4 pr-11 py-3 rounded-xl border border-slate-200 focus:border-[#7029C4] focus:ring-2 focus:ring-[#7029C4]/20 outline-none text-sm text-slate-800 bg-white placeholder:text-slate-400 transition-all shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none transition-colors"
                  tabIndex="-1"
                >
                  {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7029C4] hover:bg-[#5E21AC] text-white font-bold py-3.5 px-6 rounded-xl shadow-md shadow-[#7029C4]/25 flex items-center justify-center gap-2 text-sm transition-all cursor-pointer relative disabled:opacity-70 mt-2"
            >
              {loading ? (
                <span className="absolute inset-0 grid place-items-center bg-black/10 rounded-xl">
                  <img className="w-5 h-5" src="/assets/gif/button_loader.gif" alt="Loading" />
                </span>
              ) : (
                <>
                  <FaShieldAlt className="text-white text-sm" />
                  <span>{languageHelper("Authenticate Developer")}</span>
                </>
              )}
            </button>

            {/* Restricted Access Note */}
            <div className="pt-2 text-center flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-400">
              <FaLock className="text-slate-400 text-xs" />
              <span>{languageHelper("Restricted Developer Access")}</span>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AdministrationLogin;
